import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import TopBar from '@/components/TopBar';
import { Card, CardSm, Input, Label, Pill, Select } from '@/components/ui/Form';
import { Button } from '@/components/ui/Button';
import { Plus, Plant, Globe } from '@phosphor-icons/react';
import { REGIONS } from '@/lib/mangroveSpecies';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    project_name: '',
    country: '',
    methodology: 'VM0033',
    latitude_centroid: '',
    longitude_centroid: '',
    species_region: 'southeast_asia',
  });
  const [err, setErr] = useState('');

  const load = async () => {
    const { data } = await api.get('/projects');
    setProjects(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      const payload = {
        ...form,
        latitude_centroid: form.latitude_centroid ? Number(form.latitude_centroid) : null,
        longitude_centroid: form.longitude_centroid ? Number(form.longitude_centroid) : null,
      };
      await api.post('/projects', payload);
      setForm({ project_name: '', country: '', methodology: 'VM0033', latitude_centroid: '', longitude_centroid: '', species_region: 'all' });
      setShowForm(false);
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Create failed');
    }
  };

  return (
    <div data-testid="projects-page">
      <TopBar title="Projects" />
      <div className="p-4 space-y-3">
        <Button
          onClick={() => setShowForm(!showForm)}
          variant="dark"
          className="w-full"
          data-testid="new-project-btn"
        >
          <Plus size={20} weight="bold" />
          {showForm ? 'Close' : 'New Project'}
        </Button>

        {showForm && (
          <Card data-testid="project-form">
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label>Project name</Label>
                <Input
                  value={form.project_name}
                  onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                  required
                  data-testid="project-name-input"
                  placeholder="e.g. Sundarbans Restoration"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  required
                  data-testid="project-country-input"
                  placeholder="Bangladesh"
                />
              </div>
              <div>
                <Label>Methodology</Label>
                <Input
                  value={form.methodology}
                  onChange={(e) => setForm({ ...form, methodology: e.target.value })}
                  data-testid="project-method-input"
                />
              </div>
              <div>
                <Label>Species region (filters tree species dropdown)</Label>
                <Select
                  value={form.species_region}
                  onChange={(e) => setForm({ ...form, species_region: e.target.value })}
                  data-testid="project-region-input"
                >
                  {REGIONS.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Centroid lat</Label>
                  <Input
                    type="number" step="any"
                    value={form.latitude_centroid}
                    onChange={(e) => setForm({ ...form, latitude_centroid: e.target.value })}
                    placeholder="21.95"
                    data-testid="project-lat-input"
                  />
                </div>
                <div>
                  <Label>Centroid lon</Label>
                  <Input
                    type="number" step="any"
                    value={form.longitude_centroid}
                    onChange={(e) => setForm({ ...form, longitude_centroid: e.target.value })}
                    placeholder="89.18"
                    data-testid="project-lon-input"
                  />
                </div>
              </div>
              {err && (
                <div className="border-2 border-status-severe bg-red-50 px-3 py-2 text-sm text-status-severe">
                  {err}
                </div>
              )}
              <Button type="submit" className="w-full" data-testid="create-project-submit">
                Create project
              </Button>
            </form>
          </Card>
        )}

        {projects.length === 0 ? (
          <CardSm>
            <p className="text-sm text-carbon-soft">No projects yet.</p>
          </CardSm>
        ) : (
          <ul className="space-y-3" data-testid="project-list">
            {projects.map((p) => (
              <li key={p.id}>
                <Link to={`/projects/${p.id}`} className="block card-hard-sm p-4 hover:bg-surface-alt" data-testid={`project-${p.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Plant size={16} weight="bold" className="text-brand-primary" />
                    <span className="font-display font-bold">{p.project_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs mono text-carbon-soft">
                    <Globe size={14} />
                    <span>{p.country}</span>
                    {p.species_region && p.species_region !== 'all' && (
                      <span className="px-1.5 py-0.5 border border-carbon-soft text-[10px] uppercase tracking-widest"
                            data-testid={`project-region-${p.id}`}>
                        {p.species_region.replace('_', ' ')}
                      </span>
                    )}
                    <Pill color="#2D6A4F" className="ml-auto">{p.methodology}</Pill>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
